import { isToday, isYesterday } from 'date-fns'
import { getDisplayName } from '~/entities/reviewer'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { formatDateDefault, formatTimeDefault } from '~/shared/lib/datetime'
import {
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  IconButton,
  LocalIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Text,
  Textarea,
  Tooltip,
} from '~/shared/ui'

interface CommentsPopoverProps {
  comments: apiClient.AuditEvent[]
  isReadOnly: boolean
  commentText: string
  onCommentChange: (value: string) => void
  visible: boolean
}

export function CommentsPopover({
  comments,
  isReadOnly,
  commentText,
  onCommentChange,
  visible,
}: CommentsPopoverProps) {
  if (!visible) return null

  const hasComments = comments.length > 0

  return (
    <Popover>
      <Tooltip content={hasComments ? 'View comments' : 'Add comment'}>
        <PopoverTrigger asChild>
          <span className="absolute -top-0.5 -left-2">
            <IconButton
              materialIcon="comment"
              size="medium"
              container="medium"
            />
          </span>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        align="end"
        className="flex w-[300px] flex-col gap-2 p-3"
      >
        {hasComments ? (
          <CollapsibleRoot defaultOpen>
            <CollapsibleTrigger
              label="Comments"
              counter={comments.length}
            />
            <CollapsibleContent>
              <ScrollArea viewportClassName="max-h-[200px]">
                <div className="mt-3 flex flex-col gap-3">
                  {comments.map((event) => {
                    const date = new Date(event.timestamp)
                    return (
                      <div
                        key={event.key}
                        className="flex flex-col py-1"
                      >
                        <div className="flex gap-1">
                          <div className="flex w-full items-center justify-between gap-1">
                            <div className="flex min-w-0 items-start gap-1">
                              <div className="text-text-secondary flex shrink-0 items-center gap-2 whitespace-nowrap">
                                <Text
                                  className="uppercase"
                                  appearance="CAPTION"
                                >
                                  {formatTimeDefault(date.getTime())}
                                </Text>
                                <span className="bg-text-secondary h-1 w-1 rounded-full" />
                                <Text
                                  className="uppercase"
                                  appearance="CAPTION"
                                  title={
                                    isToday(date) || isYesterday(date)
                                      ? formatDateDefault(date)
                                      : undefined
                                  }
                                >
                                  {isToday(date)
                                    ? 'Today'
                                    : isYesterday(date)
                                      ? 'Yesterday'
                                      : formatDateDefault(date)}
                                </Text>
                                <LocalIcon
                                  name="personOutlined"
                                  size="16"
                                />
                              </div>
                              <Text
                                className="overflow-hidden"
                                appearance="CAPTION"
                              >
                                {getDisplayName(event.user ?? undefined)}
                              </Text>
                            </div>
                          </div>
                        </div>
                        {event.notes && (
                          <Text
                            className="mt-2"
                            appearance="CAPTION"
                          >
                            {event.notes}
                          </Text>
                        )}
                      </div>
                    )
                  })}
                </div>
              </ScrollArea>
            </CollapsibleContent>
          </CollapsibleRoot>
        ) : (
          <Text appearance="OVERLINE">Comments</Text>
        )}

        {!isReadOnly && (
          <Textarea
            className="mt-2"
            value={commentText}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder="Add context for any discrepancies or issues"
            rows={3}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
